import React, {useCallback, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  Button,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TimelineIcon from "@mui/icons-material/Timeline";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ScienceIcon from "@mui/icons-material/Science";
import OpacityIcon from "@mui/icons-material/Opacity";
import HealingIcon from "@mui/icons-material/Healing";
import WarningIcon from "@mui/icons-material/WarningAmber";
import BiotechIcon from "@mui/icons-material/Biotech";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {getPondSummary, clearPondCache, getCycles, PondCycle} from "../../api/pond";
import {SurvivalPredictionResponse} from "../../api/ai";
import CycleTab from "./CycleTab";
import FeedTab from "./FeedTab";
import SamplingTab from "./SamplingTab";
import MeasurementTab from "./MeasurementTab";
import TreatmentTab from "./TreatmentTab";
import MortalityTab from "./MortalityTab";
import DiseaseTab from "./DiseaseTab";
import FeedingTrayTab from "./FeedingTrayTab";
import HarvestTab from "./HarvestTab";
import PondPerformanceAnalytics from "../../components/common/PondPerformanceAnalytics";
import EnhancedPondAnalytics from "../../components/common/EnhancedFCRAnalysis";
import {
  formatSurvivalRange,
  formatSurvivalRateRange,
  formatBiomassRange,
  formatAIPredictedCount,
  formatInrIn,
  formatInrLakhLabel,
} from "../../utils/numberFormat";

/** ABW (g) below this: show “still growing” — harvest not profitable yet (product rule). */
const ABW_HARVEST_VIABILITY_G = 8;
import {useTranslation} from "react-i18next";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`pond-tabpanel-${index}`} aria-labelledby={`pond-tab-${index}`} {...other}>
      {value === index && (
        <Box
          sx={{
            p: 2,
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
                transform: "translateY(10px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PondDetailPage: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const {t, i18n} = useTranslation();
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [showStockingValueAnalysis, setShowStockingValueAnalysis] = useState(false);
  const [predictedSurvival, setPredictedSurvival] = useState<SurvivalPredictionResponse | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [cycles, setCycles] = useState<PondCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string | undefined>(undefined);

  // Fetch cycles and set default
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const cyclesData = await getCycles(id!);
        setCycles(cyclesData.data || cyclesData);

        // Find latest active cycle, or latest cycle if no active
        const activeCycle =
          cyclesData.data?.find((c: PondCycle) => c.status === "active") || cyclesData.find((c: PondCycle) => c.status === "active");
        const latestCycle = cyclesData.data?.[0] || cyclesData[0];

        const defaultCycle = activeCycle || latestCycle;
        if (defaultCycle) {
          setSelectedCycleId(defaultCycle.id);
        }
      } catch (error) {
        console.error("Error fetching cycles:", error);
      }
    };

    if (id) {
      fetchCycles();
    }
  }, [id]);

  const applyAiFromSummary = useCallback((summaryData: any) => {
    if (summaryData?.performanceMetrics?.aiAnalysis) {
      setAiAnalysis(summaryData.performanceMetrics.aiAnalysis);
      if (summaryData.performanceMetrics.aiAnalysis.predictedSurvival !== undefined) {
        setPredictedSurvival(summaryData.performanceMetrics.aiAnalysis);
      }
    } else if (summaryData?.aiAnalysis) {
      setAiAnalysis(summaryData.aiAnalysis);
      if (summaryData.aiAnalysis.predictedSurvival !== undefined) {
        setPredictedSurvival(summaryData.aiAnalysis);
      }
    } else {
      setAiAnalysis(null);
      setPredictedSurvival(null);
    }
  }, []);

  const refreshPondAndCycles = useCallback(async () => {
    if (!id || !selectedCycleId) return;
    try {
      const cyclesData = await getCycles(id);
      setCycles(cyclesData.data || cyclesData);
      const summaryData = await getPondSummary(id, selectedCycleId);
      setSummary(summaryData);
      applyAiFromSummary(summaryData);
    } catch (e) {
      console.error("refreshPondAndCycles", e);
    }
  }, [id, selectedCycleId, applyAiFromSummary]);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const summaryData = await getPondSummary(id!, selectedCycleId);
        console.log("Pond summary data received:", summaryData); // Debug log
        setSummary(summaryData);

        // Debug: Log the full summary data structure
        console.log("Full summary data:", summaryData);
        console.log("Performance metrics:", summaryData?.performanceMetrics);
        console.log("AI analysis in performance metrics:", summaryData?.performanceMetrics?.aiAnalysis);
        console.log("Performance metrics error:", summaryData?.performanceMetrics?.error);
        console.log("Current cycle:", summaryData?.currentCycle);

        applyAiFromSummary(summaryData);
      } catch (error) {
        console.error("Error fetching pond summary:", error);
        setError("Failed to load pond summary");
      } finally {
        setLoading(false);
      }
    };

    // Avoid fetching with undefined cycleId after cycles are loaded, which can
    // cause a second response to overwrite metrics with a different calculation scope.
    if (!id) return;
    if (cycles.length > 0 && !selectedCycleId) return;
    fetchSummary();
  }, [id, selectedCycleId, cycles.length, applyAiFromSummary]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!summary) {
    return <Alert severity="warning">No pond data available</Alert>;
  }

  // Safely destructure with fallbacks
  const pond = summary.pond || {};
  const performanceMetrics = summary.performanceMetrics || {};
  const currentCycle = summary.currentCycle;
  const stockingValueAnalysis = summary.stockingValueAnalysis || {};
  const abwGrams = Number(performanceMetrics.abw);
  const hasMeaningfulAbw = !Number.isNaN(abwGrams) && abwGrams > 0;
  const isGrowingByAbw =
    !stockingValueAnalysis.missingReason && hasMeaningfulAbw && abwGrams < ABW_HARVEST_VIABILITY_G;

  // Determine if selected cycle is active
  const selectedCycle = cycles.find((c) => c.id === selectedCycleId) || currentCycle;
  const isCycleActive = selectedCycle?.status === "active";

  // Debug: Log the performance metrics data
  console.log("Performance Metrics Debug:", {
    performanceMetrics,
    totalFeed: performanceMetrics.totalFeed,
    abw: performanceMetrics.abw,
    fcr: performanceMetrics.fcr,
    biomassRange: performanceMetrics.biomassRange,
    age: performanceMetrics.age,
    selectedCycleId,
    isCycleActive,
  });

  // Check if we have the essential pond data
  if (!pond.id || !pond.name) {
    return <Alert severity="error">Invalid pond data received</Alert>;
  }

  // Format cycle display name
  const formatCycleName = (cycle: PondCycle) => {
    const startDate = new Date(cycle.startedAt).toLocaleDateString();
    const endDate = cycle.finishedAt ? new Date(cycle.finishedAt).toLocaleDateString() : null;
    const statusLabel = cycle.status === "active" ? t("Active") : cycle.status === "completed" ? t("Completed") : t("Terminated");
    return `${startDate}${endDate ? ` - ${endDate}` : ""} (${statusLabel})`;
  };

  return (
    <Box p={{xs: 2, sm: 3}} sx={{overflowX: "auto", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box"}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h5" gutterBottom>
            {pond.name}
          </Typography>
          {cycles.length > 0 && (
            <FormControl size="small" sx={{minWidth: 250}}>
              <InputLabel>{t("Cycle")}</InputLabel>
              <Select value={selectedCycleId || ""} label={t("Cycle")} onChange={(e) => setSelectedCycleId(e.target.value)}>
                {cycles.map((cycle) => (
                  <MenuItem key={cycle.id} value={cycle.id}>
                    {formatCycleName(cycle)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Button
          variant="outlined"
          color="primary"
          onClick={async () => {
            try {
              await clearPondCache(id!);
              // Refresh the data
              const summaryData = await getPondSummary(id!, selectedCycleId);
              setSummary(summaryData);
              if (summaryData?.performanceMetrics?.aiAnalysis) {
                setAiAnalysis(summaryData.performanceMetrics.aiAnalysis);
                if (summaryData.performanceMetrics.aiAnalysis.predictedSurvival !== undefined) {
                  setPredictedSurvival(summaryData.performanceMetrics.aiAnalysis);
                }
              } else if (summaryData?.aiAnalysis) {
                setAiAnalysis(summaryData.aiAnalysis);
                if (summaryData.aiAnalysis.predictedSurvival !== undefined) {
                  setPredictedSurvival(summaryData.aiAnalysis);
                }
              } else {
                setAiAnalysis(null);
                setPredictedSurvival(null);
              }
            } catch (error) {
              console.error("Error clearing cache:", error);
            }
          }}>
          Refresh Data
        </Button>
      </Box>
      {!isCycleActive && selectedCycle && (
        <Alert severity="info" sx={{mb: 2}}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}
      <Box display="flex" gap={0.5} mb={1} flexWrap="wrap" alignItems="center" sx={{overflowX: "auto", minWidth: 0}}>
        {/* AGE */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("AGE")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.age || 0}
          </Typography>
        </Paper>

        {/* STOCKING */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("STOCKING")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {summary.currentCycle?.totalSeed ? summary.currentCycle.totalSeed.toLocaleString() : "-"}
          </Typography>
        </Paper>

        {/* AI PREDICTED SURVIVAL */}
        <Paper
          sx={{
            p: {xs: 0.5, md: 0.5, lg: 1},
            minWidth: {xs: 80, md: 80, lg: 100},
            flex: 1.2,
            textAlign: "center",
            background: "#f6fff6",
          }}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.4rem", md: "0.4rem", lg: "0.5rem"}} color="success.main">
            {t("AI PREDICTED")}
          </Typography>
          <Typography display={"block"} variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}} color="success.main">
            {t("SURVIVAL")}
          </Typography>
          {(() => {
            console.log("AI PREDICTED SURVIVAL Debug:", {
              predictedSurvival,
              hasPredictedSurvival: !!predictedSurvival,
              predictedSurvivalValue: predictedSurvival?.predictedSurvival,
              isNumber: typeof predictedSurvival?.predictedSurvival === "number",
              isValid: predictedSurvival?.predictedSurvival !== null && predictedSurvival?.predictedSurvival !== undefined,
            });

            return predictedSurvival ? (
              <>
                <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}} color="success.main">
                  {predictedSurvival.predictedSurvival !== null && predictedSurvival.predictedSurvival !== undefined
                    ? `${Number(predictedSurvival.predictedSurvival).toFixed(2)}%`
                    : "-"}
                </Typography>
                <Typography variant="body2" fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}} color="success.main">
                  {predictedSurvival.predictedSurvival !== null && predictedSurvival.predictedSurvival !== undefined
                    ? formatAIPredictedCount(Number(predictedSurvival.predictedSurvival), summary.currentCycle?.totalSeed || 0, i18n.language)
                    : "-"}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}} color="text.secondary">
                  {t("N/A")}
                </Typography>
                <Typography variant="body2" fontSize={{xs: "0.5rem", md: "0.5rem", lg: "0.6rem"}} color="text.secondary">
                  {t("AI not configured")}
                </Typography>
              </>
            );
          })()}
        </Paper>

        {/* BIOMASS */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 80, md: 80, lg: 100}, flex: 1.5, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("BIOMASS")}
          </Typography>
          <br />
          <Typography
            variant="body2"
            fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}
            sx={{
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}>
            {formatBiomassRange(performanceMetrics.biomassRange)}
          </Typography>
        </Paper>

        {/* FEED */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("FEED")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.totalFeed ? Math.round(Number(performanceMetrics.totalFeed)) : "-"} kg
          </Typography>
        </Paper>

        {/* ABW */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("ABW")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.abw ? Number(performanceMetrics.abw).toFixed(2) : "-"}
          </Typography>
        </Paper>

        {/* FCR */}
        <Paper sx={{p: {xs: 0.5, md: 0.5, lg: 1}, minWidth: {xs: 60, md: 60, lg: 80}, flex: 1, textAlign: "center"}}>
          <Typography variant="caption" fontWeight={700} fontSize={{xs: "0.6rem", md: "0.6rem", lg: "0.7rem"}}>
            {t("FCR")}
          </Typography>
          <br />
          <Typography variant="body2" fontSize={{xs: "0.7rem", md: "0.7rem", lg: "0.9rem"}}>
            {performanceMetrics.fcr ? Number(performanceMetrics.fcr).toFixed(2) : "-"}
          </Typography>
          {performanceMetrics.templateFCR && (
            <Typography variant="caption" fontSize={{xs: "0.5rem", md: "0.5rem", lg: "0.6rem"}} color="text.secondary">
              Template: {Number(performanceMetrics.templateFCR).toFixed(2)}
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Current Stocking Value Analysis */}
      <Box my={3}>
        <Paper sx={{p: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa"}}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{mb: showStockingValueAnalysis ? 1 : 0}}>
            <Box flex={1} minWidth={0} display="flex" flexWrap="wrap" alignItems="baseline" columnGap={1} rowGap={0.25}>
              <Typography variant="h6" color="primary" fontWeight={600} component="span" sx={{display: "inline"}}>
                {t("Current Stocking Value Analysis")}
              </Typography>
              {!showStockingValueAnalysis && !stockingValueAnalysis.missingReason && (
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} columnGap={1.5} minWidth={0}>
                  {stockingValueAnalysis.currentStockValue != null ? (
                    <Typography component="span" variant="body2" sx={{whiteSpace: "nowrap"}}>
                      💰 {t("Expected sale")}: {formatInrLakhLabel(Number(stockingValueAnalysis.currentStockValue))}
                    </Typography>
                  ) : null}
                  {stockingValueAnalysis.totalProductionCost != null ? (
                    <Typography component="span" variant="body2" sx={{whiteSpace: "nowrap"}}>
                      💸 {t("Total investment")}: {formatInrLakhLabel(Number(stockingValueAnalysis.totalProductionCost))}
                    </Typography>
                  ) : null}
                  {isGrowingByAbw ? (
                    <Typography component="span" variant="caption" color="warning.dark" fontWeight={600}>
                      🟡 {t("cropGrowingNoProfitYet")}
                    </Typography>
                  ) : stockingValueAnalysis.profitOrLoss != null ? (
                    (() => {
                      const pl = Number(stockingValueAnalysis.profitOrLoss);
                      if (pl > 0) {
                        return (
                          <Typography component="span" variant="subtitle2" color="success.main" fontWeight={700}>
                            ✓ {t("Profitable")} · {t("Estimated Profit")}: {formatInrIn(pl)}
                          </Typography>
                        );
                      }
                      return (
                        <Typography component="span" variant="subtitle2" color="error.main" fontWeight={700}>
                          {t("Loss if harvested now")}: {formatInrIn(Math.abs(pl))}
                        </Typography>
                      );
                    })()
                  ) : null}
                </Box>
              )}
            </Box>
            <IconButton
              onClick={() => setShowStockingValueAnalysis((v) => !v)}
              size="small"
              sx={{color: "primary.main", flexShrink: 0}}
              aria-expanded={showStockingValueAnalysis}
              aria-label={
                showStockingValueAnalysis
                  ? t("Collapse stocking value analysis", {defaultValue: "Collapse stocking value analysis"})
                  : t("Expand stocking value analysis", {defaultValue: "Expand stocking value analysis"})
              }>
              {showStockingValueAnalysis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showStockingValueAnalysis}>
          {stockingValueAnalysis.missingReason === "metrics_error" && (
            <Alert severity="warning" sx={{mb: 1}}>
              {String(stockingValueAnalysis.error || t("Could not compute performance metrics for this cycle."))}
            </Alert>
          )}
          {stockingValueAnalysis.missingReason === "market_mapping" && (
            <Alert severity="info" sx={{mb: 1}}>
              {t("Add stocking price and select market area and commodity in the Feed tab to enable value analysis.")}
            </Alert>
          )}
          {stockingValueAnalysis.missingReason === "abw" && (
            <Alert severity="info" sx={{mb: 1}}>
              {t("ABW is required to estimate count per kg for market matching. Add sampling records or wait until biomass can be estimated.")}
            </Alert>
          )}
          {stockingValueAnalysis.missingReason === "no_price_row" && (
            <Alert severity="warning" sx={{mb: 1}}>
              {t("No COUNT_WISE market price rows found for this area and commodity. Configure prices in Market Prices.")}
            </Alert>
          )}
          {stockingValueAnalysis.missingReason === "no_price_in_count_range" && (
            <>
              <Alert severity="warning" sx={{mb: 1}}>
                {t("stockingPricingOutOfRange", {
                  defaultValue: "Pricing unavailable: no price found for count ({{count}})/kg. Please contact support.",
                  count: stockingValueAnalysis.approxCountPerKg ?? "—",
                  tol: stockingValueAnalysis.countMatchTolerance ?? 5,
                })}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                {t("Estimated biomass (kg)")}:{" "}
                <strong>{stockingValueAnalysis.currentBiomassKg != null ? Number(stockingValueAnalysis.currentBiomassKg).toFixed(2) : "—"}</strong>
                {" · "}
                {t("Approx. count/kg")}: <strong>{stockingValueAnalysis.approxCountPerKg ?? "—"}</strong>
              </Typography>
            </>
          )}
          {stockingValueAnalysis.missingReason === "no_cycle" && <Alert severity="info">{t("No cycle selected.")}</Alert>}
          {!stockingValueAnalysis.missingReason && (
            <Box display="flex" flexDirection="column" gap={2}>
              {stockingValueAnalysis.currentStockValue != null && (
                <Typography variant="subtitle1" fontWeight={700} sx={{lineHeight: 1.4}}>
                  💰 {t("Expected sale")}:{" "}
                  <Box component="span" color="success.dark">
                    {formatInrLakhLabel(Number(stockingValueAnalysis.currentStockValue))}
                  </Box>
                </Typography>
              )}
              {stockingValueAnalysis.totalProductionCost != null && (
                <Typography variant="subtitle1" fontWeight={700} sx={{lineHeight: 1.4}}>
                  💸 {t("Total investment")}:{" "}
                  <Box component="span" color="text.primary">
                    {formatInrLakhLabel(Number(stockingValueAnalysis.totalProductionCost))}
                  </Box>
                </Typography>
              )}

              {isGrowingByAbw ? (
                <Alert severity="warning" icon={false} sx={{py: 1.25, borderLeft: 4, borderColor: "warning.main"}}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    🟡 {t("cropGrowingNoProfitYet")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("cropGrowingAbwHint", {
                      defaultValue: "Average body weight is under {{grams}} g — typical harvest economics improve as the crop grows.",
                      grams: ABW_HARVEST_VIABILITY_G,
                    })}
                  </Typography>
                </Alert>
              ) : stockingValueAnalysis.profitOrLoss != null ? (
                (() => {
                  const pl = Number(stockingValueAnalysis.profitOrLoss);
                  if (pl > 0) {
                    return (
                      <Alert severity="success" icon={false} sx={{py: 1.25, borderLeft: 4, borderColor: "success.main"}}>
                        <Typography variant="subtitle1" fontWeight={800} color="success.dark">
                          {t("Profitable")}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main" sx={{mt: 0.5}}>
                          {t("Estimated Profit")}: {formatInrIn(pl)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                          {t("If harvested now")}
                        </Typography>
                      </Alert>
                    );
                  }
                  return (
                    <Alert severity="info" icon={false} sx={{py: 1.25}}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t("Not profitable if harvested now")}
                      </Typography>
                      <Typography variant="body2" sx={{mt: 0.5}}>
                        {t("Estimated shortfall")}: {formatInrIn(Math.abs(pl))}
                      </Typography>
                    </Alert>
                  );
                })()
              ) : null}

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" gutterBottom>
                  💸 {t("expenseSummarySimple")}
                </Typography>
                <Paper variant="outlined" sx={{p: 1.5, bgcolor: "#fff", borderRadius: 1}}>
                  {[
                    {
                      label: t("Seed cost (live stock)"),
                      value:
                        stockingValueAnalysis.initialLiveStockCost != null
                          ? Number(stockingValueAnalysis.initialLiveStockCost)
                          : null,
                      always: true,
                    },
                    {
                      label: t("Feed cost"),
                      value:
                        stockingValueAnalysis.estimatedFeedCost != null
                          ? Number(stockingValueAnalysis.estimatedFeedCost)
                          : null,
                      always: true,
                    },
                    {
                      label: t("Medicine cost"),
                      value:
                        stockingValueAnalysis.medicineCost != null ? Number(stockingValueAnalysis.medicineCost) : null,
                      always: false,
                    },
                    {
                      label: t("Electricity"),
                      value:
                        stockingValueAnalysis.powerBills != null ? Number(stockingValueAnalysis.powerBills) : null,
                      always: false,
                    },
                    {
                      label: t("Salaries"),
                      value: stockingValueAnalysis.salaries != null ? Number(stockingValueAnalysis.salaries) : null,
                      always: false,
                    },
                    {
                      label: t("Other costs"),
                      value:
                        stockingValueAnalysis.otherProductionCosts != null
                          ? Number(stockingValueAnalysis.otherProductionCosts)
                          : null,
                      always: false,
                    },
                  ]
                    .filter((row) => row.always || (row.value != null && row.value > 0))
                    .map((row, index) => (
                      <Box
                        key={row.label}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        py={0.85}
                        px={1.25}
                        mx={-1.25}
                        sx={{
                          borderRadius: 0.75,
                          bgcolor: (theme) =>
                            index % 2 === 0
                              ? theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.06)"
                                : theme.palette.grey[50]
                              : theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : theme.palette.grey[100],
                        }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.label}:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} textAlign="right">
                          {row.value != null && !Number.isNaN(Number(row.value))
                            ? formatInrIn(Number(row.value))
                            : "—"}
                        </Typography>
                      </Box>
                    ))}
                  {stockingValueAnalysis.totalProductionCost != null ? (
                    <>
                      <Divider sx={{my: 1}} />
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                        <Typography variant="body1" fontWeight={800}>
                          👉 {t("Total investment")}:
                        </Typography>
                        <Typography variant="body1" fontWeight={800} color="primary.main">
                          {formatInrIn(Number(stockingValueAnalysis.totalProductionCost))}
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </Paper>
              </Box>

              <Box
                sx={{
                  pt: 0.5,
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}>
                <Typography variant="caption">
                  {t("ABW")}:{" "}
                  <strong>{hasMeaningfulAbw ? `${abwGrams} g` : "—"}</strong>
                  {" · "}
                  {t("Estimated biomass (kg)")}:{" "}
                  <strong>
                    {stockingValueAnalysis.currentBiomassKg != null
                      ? Number(stockingValueAnalysis.currentBiomassKg).toFixed(2)
                      : "—"}
                  </strong>
                  {" · "}
                  {t("Approx. count/kg")}: <strong>{stockingValueAnalysis.approxCountPerKg ?? "—"}</strong>
                </Typography>
                {stockingValueAnalysis.selectedMarketPrice && (
                  <Typography variant="caption">
                    {t("Market price used")}: {stockingValueAnalysis.selectedMarketPrice.count}C
                    {stockingValueAnalysis.selectedMarketPrice.label
                      ? ` (${stockingValueAnalysis.selectedMarketPrice.label})`
                      : ""}{" "}
                    @ {formatInrIn(Number(stockingValueAnalysis.selectedMarketPrice.pricePerKg))}/{t("kg")}
                    {stockingValueAnalysis.matchedPreferredLabel === false && stockingValueAnalysis.marketLabel ? (
                      <Chip size="small" label={t("Label fallback")} sx={{ml: 1, height: 20, fontSize: "0.65rem"}} />
                    ) : null}
                  </Typography>
                )}
                {stockingValueAnalysis.missingInitialPrice ? (
                  <Chip size="small" color="warning" label={t("Add stocking price in Feed tab")} sx={{alignSelf: "flex-start", height: 24}} />
                ) : null}
              </Box>
            </Box>
          )}
          </Collapse>
        </Paper>
      </Box>

      {/* AI Recommendations Section */}
      {(() => {
        // Check for AI data from dedicated AI analysis endpoint
        const aiData = aiAnalysis || predictedSurvival;
        const hasAiData = aiData && (aiAnalysis?.enabled || predictedSurvival);

        console.log("AI Analysis Debug:", {
          hasAiAnalysis: !!aiAnalysis,
          hasPredictedSurvival: !!predictedSurvival,
          aiAnalysis: aiAnalysis,
          predictedSurvival: predictedSurvival,
          aiData: aiData,
          hasAiData: hasAiData,
          isEnabled: aiAnalysis?.enabled,
          hasAnalysis: !!aiData?.analysis,
          hasRecommendations: !!aiData?.recommendations?.length,
          hasInsights: !!aiData?.insights?.length,
        });

        if (hasAiData && aiData.analysis) {
          return (
            <Box my={3}>
              <Paper sx={{p: 3, backgroundColor: "#f8f9fa", border: "2px solid #e3f2fd"}}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" color="primary" fontWeight={600}>
                    {`🤖 ${t("AI Recommendations")}`}
                  </Typography>
                  <IconButton onClick={() => setShowAIRecommendations(!showAIRecommendations)} size="small" sx={{color: "primary.main"}}>
                    {showAIRecommendations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={showAIRecommendations}>
                  {/* AI Recommendations */}
                  {aiData.recommendations && aiData.recommendations.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom color="success.main">
                        🎯 Actionable Recommendations
                      </Typography>
                      <Box component="ul" sx={{pl: 2, m: 0}}>
                        {aiData.recommendations.map((recommendation: string, index: number) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              lineHeight: 1.5,
                              "&::marker": {color: "success.main"},
                            }}>
                            {recommendation}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* AI Insights */}
                  {aiData.insights && aiData.insights.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom color="info.main">
                        💡 Key Insights
                      </Typography>
                      <Box component="ul" sx={{pl: 2, m: 0}}>
                        {aiData.insights.map((insight: string, index: number) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              lineHeight: 1.5,
                              "&::marker": {color: "info.main"},
                            }}>
                            {insight}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </Paper>
            </Box>
          );
        } else {
          // Show a more helpful message when AI analysis is not available
          return (
            <Box my={3}>
              <Paper sx={{p: 3, backgroundColor: "#fff3e0", border: "2px solid #ff9800"}}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6" color="warning.main" fontWeight={600}>
                    🤖 AI Recommendations
                  </Typography>
                  <Chip label="Info: AI-001" color="warning" size="small" variant="outlined" />
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{mt: 1}}>
                  AI recommendations are not available at this time. This could be due to:
                </Typography>
                <Box component="ul" sx={{pl: 2, mt: 1, mb: 0}}>
                  <Typography component="li" variant="body2" color="textSecondary">
                    • No AI services configured (Gemini, OpenAI, or Ollama)
                  </Typography>
                  <Typography component="li" variant="body2" color="textSecondary">
                    • Insufficient data for AI analysis
                  </Typography>
                  <Typography component="li" variant="body2" color="textSecondary">
                    • AI services temporarily unavailable
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{mt: 1, fontStyle: "italic"}}>
                  Performance metrics are still available and accurate.
                </Typography>
              </Paper>
            </Box>
          );
        }
      })()}

      {/* Professional Tab menu wrapper with 1024px constraint */}
      <Box
        position="relative"
        sx={{
          maxWidth: "1024px",
          overflowX: "auto",
          whiteSpace: "nowrap",
          mb: 3,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          p: 1,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          "&::-webkit-scrollbar": {height: 6},
          "&::-webkit-scrollbar-track": {backgroundColor: "#f5f5f5", borderRadius: 3},
          "&::-webkit-scrollbar-thumb": {backgroundColor: "#c1c1c1", borderRadius: 3, "&:hover": {backgroundColor: "#a8a8a8"}},
        }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minWidth: 0,
            ".MuiTabs-flexContainer": {flexWrap: "nowrap", gap: 0.5},
            ".MuiTab-root": {
              minWidth: "auto",
              minHeight: "40px",
              padding: "8px 12px",
              fontSize: {xs: "0.7rem", sm: "0.75rem", md: "0.8rem"},
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "6px",
              margin: "0 2px",
              transition: "all 0.2s ease",
              "&.Mui-selected": {
                backgroundColor: "#388e3c",
                color: "#ffffff",
                boxShadow: "0 2px 4px rgba(56,142,60,0.3)",
                transform: "translateY(-1px)",
              },
              "&:hover": {backgroundColor: "#f5f5f5", "&.Mui-selected": {backgroundColor: "#2e7d32"}},
            },
            ".MuiTabs-scrollButtons": {width: "24px", height: "24px", "&.Mui-disabled": {opacity: 0.3}},
            ".MuiTabs-indicator": {display: "none"},
          }}>
          <Tab label={t("Cycle")} />
          <Tab label={t("Feed")} />
          <Tab label={t("Sampling")} />
          <Tab label={t("Mortality")} />
          <Tab label={t("Water Quality")} />
          <Tab label={t("Disease")} />
          <Tab label={t("Feeding Tray")} />
          <Tab label={t("Treatment")} />
          <Tab label={t("Harvest")} />
          <Tab label={t("Analytics")} />
        </Tabs>
      </Box>
      <TabPanel value={tab} index={0}>
        <CycleTab pondId={id!} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <FeedTab
          pondId={id!}
          cycleId={selectedCycleId}
          isCycleActive={isCycleActive}
          cycleStartDate={selectedCycle?.startedAt}
          selectedCycle={selectedCycle}
          onCycleUpdated={refreshPondAndCycles}
        />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <SamplingTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <MortalityTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <MeasurementTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <DiseaseTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        <FeedingTrayTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={7}>
        <TreatmentTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={8}>
        <HarvestTab pondId={id!} cycleId={selectedCycleId} isCycleActive={isCycleActive} />
      </TabPanel>
      <TabPanel value={tab} index={9}>
        <Box>
          <EnhancedPondAnalytics
            aiAnalysis={performanceMetrics.aiAnalysis}
            performanceMetrics={performanceMetrics}
            loading={loading}
            cycleId={selectedCycleId}
          />
          <Box mt={4}>
            <PondPerformanceAnalytics pondId={id!} cycleId={selectedCycleId} performanceMetrics={performanceMetrics} />
          </Box>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default PondDetailPage;
